import type { VisibilityRule } from '@sola/schema/lib/schema.ts';

export type VisibilityContext = {
  experiments?: string[];
  loggedIn?: boolean;
  knownSupporter?: boolean;
};

const authConditions: ReadonlySet<string> = new Set<string>([
  'loggedIn',
  'knownSupporter',
]);

/** Whether a rule requires client-side auth context to evaluate. */
const isAuthRule = (r: VisibilityRule): boolean =>
  authConditions.has(r.condition);

const matchesCondition = (
  rule: VisibilityRule,
  context: VisibilityContext,
): boolean => {
  switch (rule.condition) {
    case 'experiment': {
      return context.experiments?.includes(rule.value ?? '') ?? false;
    }
    case 'loggedIn': {
      return context.loggedIn === rule.value;
    }
    case 'knownSupporter': {
      return context.knownSupporter === rule.value;
    }
    default: {
      return false;
    }
  }
};

/**
 * Evaluates visibility rules against the current context.
 * Returns true if the component should be hidden.
 *
 * Priority model:
 * 1. Auth rules (loggedIn, knownSupporter) are evaluated first and are absolute.
 *    - If any auth hide rule matches → hidden (experiments cannot override).
 *    - If auth show rules exist and none match → hidden (experiments cannot override).
 * 2. Experiment rules are secondary — they further filter within the auth-allowed set.
 *    - If any experiment hide rule matches → hidden.
 *    - If experiment show rules exist and none match → hidden.
 */
type RuleAccumulator = {
  hideMatch: boolean;
  showExists: boolean;
  showMatch: boolean;
};

const accumulateRules = (
  rules: VisibilityRule[],
  context: VisibilityContext,
  predicate: (r: VisibilityRule) => boolean,
): RuleAccumulator => {
  const acc: RuleAccumulator = {
    hideMatch: false,
    showExists: false,
    showMatch: false,
  };
  for (const rule of rules) {
    if (!predicate(rule)) {
      continue;
    }
    const matches = matchesCondition(rule, context);
    if (rule.action === 'hide' && matches) {
      acc.hideMatch = true;
    }
    if (rule.action === 'show') {
      acc.showExists = true;
      if (matches) {
        acc.showMatch = true;
      }
    }
  }
  return acc;
};

const shouldHideByGroup = (acc: RuleAccumulator): boolean =>
  acc.hideMatch || (acc.showExists && !acc.showMatch);

export const evaluateVisibilityRules = (
  rules: VisibilityRule[] | undefined,
  context: VisibilityContext,
): boolean => {
  if (!rules?.length) {
    return false;
  }

  // Auth rules (highest priority) — absolute gate
  const auth = accumulateRules(rules, context, isAuthRule);
  if (shouldHideByGroup(auth)) {
    return true;
  }

  // Experiment rules (secondary) — further filter within auth-allowed set
  const exp = accumulateRules(rules, context, (r) => !isAuthRule(r));
  return shouldHideByGroup(exp);
};

/**
 * Checks if any visibility rules require client-side auth context
 * (loggedIn, knownSupporter) rather than server-side evaluation.
 */
export const hasClientRules = (rules?: VisibilityRule[]): boolean =>
  !!rules?.some(isAuthRule);

/**
 * Converts legacy experimentShow/experimentHide arrays into VisibilityRule[].
 * Returns undefined when there is nothing to convert.
 */
export const buildVisibilityRulesFromLegacy = (
  experimentShow?: string[],
  experimentHide?: string[],
): VisibilityRule[] | undefined => {
  const rules: VisibilityRule[] = [];
  if (experimentShow?.length) {
    for (const exp of experimentShow) {
      rules.push({ condition: 'experiment', value: exp, action: 'show' });
    }
  }
  if (experimentHide?.length) {
    for (const exp of experimentHide) {
      rules.push({ condition: 'experiment', value: exp, action: 'hide' });
    }
  }
  return rules.length ? rules : undefined;
};

/**
 * Migrates legacy experimentShow/experimentHide arrays into the unified
 * visibilityRules format. Returns undefined if there is nothing to migrate.
 */
export const migrateExperimentFields = (settings: {
  experimentShow?: string[];
  experimentHide?: string[];
  visibilityRules?: VisibilityRule[];
}): VisibilityRule[] | undefined => {
  if (settings.visibilityRules?.length) {
    return settings.visibilityRules;
  }
  return buildVisibilityRulesFromLegacy(
    settings.experimentShow,
    settings.experimentHide,
  );
};

/**
 * Whether a component with the given visibility rules is definitively hidden
 * server-side (by experiment rules only, no client-side auth needed).
 */
export const isServerHidden = (
  rules: VisibilityRule[] | undefined,
  context: VisibilityContext,
): boolean => {
  if (!rules?.length) {
    return false;
  }
  if (hasClientRules(rules)) {
    return false;
  }
  return evaluateVisibilityRules(rules, context);
};
